import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { Button, Modal, Spin, Tooltip, message } from "antd";
import { CheckCircleFilled, ExclamationCircleFilled, WarningFilled } from "@ant-design/icons";
import { memberState } from "../../state/atoms";
import YextApi, {
  ListingProvider,
  YextProfile,
  YextSyncStatus,
  YextTemporaryClosure,
  yextErrorMessage,
} from "../../api/YextApi";
import { ListingsHomeTab } from "../listings/ListingsHomeTab";
import { ListingsListingsTab } from "../listings/ListingsListingsTab";
import { ListingsReportsTab } from "../listings/ListingsReportsTab";
import { ListingsProfileTab } from "../listings/ListingsProfileTab";
import { ListingsSplash } from "../listings/ListingsSplash";
import { BusinessHoursEditor } from "../listings/BusinessHoursEditor";
import { confirmDiscardUnsaved } from "../listings/confirmDiscardUnsaved";
import { useUnsavedChangesGuard } from "../listings/useUnsavedChangesGuard";
import { useListingsAccess } from "../../hooks/useListingsAccess";
import { isProSubscription } from "../../service/BillingService";
import ListingsComingSoon from "./ListingsComingSoon";
import "./Listings.scss";

type ListingsTabKey = "status" | "menus" | "hours" | "profile" | "reports";

const TABS: { key: ListingsTabKey; label: string }[] = [
  { key: "status", label: "Status" },
  { key: "menus", label: "Menus" },
  { key: "hours", label: "Hours" },
  { key: "profile", label: "Profile" },
];

// First-time onboarding gate. Until the location has a Yext entity, we show the marketing splash,
// then (on Get Started) the Profile Identity form alone; once the entity is created we reveal the
// full dashboard. "loading" = entity status not yet known.
type Phase = "loading" | "setup" | "splash" | "onboarding" | "ready";

export default function Listings() {
  const [activeTab, setActiveTab] = useState<ListingsTabKey>("status");
  const [profileDirty, setProfileDirty] = useState(false);
  const [phase, setPhase] = useState<Phase>("loading");
  const [sync, setSync] = useState<YextSyncStatus | null>(null);
  const [profile, setProfile] = useState<YextProfile | null>(null);
  const [provider, setProvider] = useState<ListingProvider>("YEXT");
  const member = useRecoilValue(memberState);
  const listingsAllowed = useListingsAccess();
  const { locationId } = useParams();
  const locId = locationId ? Number(locationId) : undefined;

  useUnsavedChangesGuard(profileDirty);

  // Draft-vs-live sync state for the header pill. Refreshed after any draft autosave or publish.
  const refreshSync = useCallback(() => {
    if (!locId) return;
    YextApi.getSyncStatus(locId)
      .then(({ data }) => setSync(data))
      .catch(() => {});
    YextApi.getProfile(locId)
      .then(({ data }) => setProfile(data))
      .catch(() => {});
  }, [locId]);

  useEffect(() => {
    if (phase === "ready" || phase === "onboarding") refreshSync();
  }, [phase, refreshSync]);

  // Resolve the onboarding phase from whether the location already has a Yext entity.
  useEffect(() => {
    if (listingsAllowed !== true) return;
    if (!locId) {
      setPhase("ready");
      return;
    }
    let cancelled = false;
    setPhase("loading");
    YextApi.getStatus(locId)
      .then(({ data }) => {
        if (cancelled) return;
        setProvider(data.provider ?? "YEXT");
        if (data.inSetup || (data.provider === "SYNUP" && !data.hasEntity)) setPhase("setup");
        else setPhase(data.hasEntity ? "ready" : "splash");
      })
      .catch(() => {
        // On error, don't trap the user on a spinner — fall through to the dashboard.
        if (!cancelled) setPhase("ready");
      });
    return () => {
      cancelled = true;
    };
  }, [locId, listingsAllowed]);

  function selectTab(key: ListingsTabKey) {
    if (key === activeTab) return;
    // Leaving the Profile tab with unsaved edits would lose them — confirm first.
    if (activeTab === "profile" && profileDirty) {
      confirmDiscardUnsaved(() => {
        setProfileDirty(false);
        setActiveTab(key);
      });
      return;
    }
    setActiveTab(key);
  }

  function onAddBusinessListings() {
    // Opens the shared payment modal (Add Business Listings add-on flow).
    window.MHM.ProModal.addListings({
      memberId: `${member.ownerId}` || member.id,
      locationId: locId,
      onSuccess: () => window.location.reload(),
    });
  }

  if (listingsAllowed !== true) {
    return listingsAllowed === null ? (
      <div className="listings-page listings-page-loading">
        <Spin size="large" />
      </div>
    ) : (
      <ListingsComingSoon />
    );
  }

  if (!isProSubscription(member)) {
    return (
      <div className="listings-page">
        <ListingsSplash onCta={onAddBusinessListings} />
      </div>
    );
  }

  if (phase === "loading") {
    return (
      <div className="listings-page listings-page-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (phase === "setup") {
    return (
      <div className="listings-page">
        <ListingsSplash
          title="Your listings are being set up"
          subtitle="Our team is setting up your listings — you'll be able to review and publish them here once they're ready."
        />
      </div>
    );
  }

  // First-time splash: pitch + "Get Started" → reveal the Profile Identity setup.
  if (phase === "splash") {
    return (
      <div className="listings-page">
        <ListingsSplash
          ctaLabel="Get Started"
          onCta={() => setPhase("onboarding")}
          footer={<span className="listings-splash-fineprint">$60/month per location · Cancel anytime</span>}
        />
      </div>
    );
  }

  // Onboarding: only the Profile form (the same one used in the full dashboard). Publishing it
  // creates the entity in Yext, after which we drop the user into the full dashboard.
  if (phase === "onboarding") {
    return (
      <div className="listings-page">
        <header className="listings-page-header">
          <h1>Listings</h1>
        </header>
        <div className="listings-tab-content">
          <ListingsProfileTab
            locationId={locId}
            onDirtyChange={setProfileDirty}
            syncStatus={sync}
            onSyncChange={refreshSync}
            publishLabel="Start now"
            showSaveChanges
            onPublished={() => {
              // Land the user on the Profile tab (not the default Status) right after setup.
              setActiveTab("profile");
              setPhase("ready");
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="listings-page">
      <header className="listings-page-header">
        <h1>Listings</h1>
        <ClosedPill closure={sync?.temporaryClosure} />
        <SyncPill sync={sync} />
        {locId && sync && !sync.liveAt && (
          <GoLiveButton locationId={locId} profile={profile} saving={profileDirty} onLive={refreshSync} />
        )}
      </header>

      <nav className="listings-tabs">
        {TABS.filter((t) => provider !== "SYNUP" || t.key !== "menus").map((t) => (
          <button
            key={t.key}
            type="button"
            className={`listings-tab${activeTab === t.key ? " active" : ""}`}
            onClick={() => selectTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="listings-tab-content">
        {activeTab === "status" && <ListingsListingsTab locationId={locId} />}
        {activeTab === "menus" && <ListingsHomeTab locationId={locId} />}
        {activeTab === "hours" && (
          <BusinessHoursEditor locationId={locId} syncStatus={sync} onSyncChange={refreshSync} />
        )}
        {activeTab === "profile" && (
          <ListingsProfileTab
            locationId={locId}
            onDirtyChange={setProfileDirty}
            syncStatus={sync}
            onSyncChange={refreshSync}
          />
        )}
        {activeTab === "reports" && <ListingsReportsTab locationId={locId} />}
      </div>
    </div>
  );
}

const GO_LIVE_REQUIREMENTS: { label: string; met: (p: YextProfile) => boolean }[] = [
  {
    label: "Name, address and phone",
    met: (p) => [p.businessName, p.street, p.city, p.state, p.zip, p.primaryPhone].every((v) => !!v?.trim()),
  },
  {
    label: "Basic hours",
    met: (p) => Object.values(p.hours ?? {}).some((d) => d?.open && d.ranges?.length),
  },
  { label: "Categories", met: (p) => !!p.category?.length },
  { label: "Description", met: (p) => !!p.descriptionLong?.trim() },
  { label: "Cover photo", met: (p) => !!p.visualAssets?.coverPhoto },
  { label: "Logo", met: (p) => !!p.visualAssets?.logo },
];

function GoLiveButton({
  locationId,
  profile,
  saving,
  onLive,
}: {
  locationId: number;
  profile: YextProfile | null;
  saving: boolean;
  onLive: () => void;
}) {
  const [publishing, setPublishing] = useState(false);
  const missing = profile ? GO_LIVE_REQUIREMENTS.filter((r) => !r.met(profile)).map((r) => r.label) : [];
  const ready = !!profile && !missing.length && !saving;

  function goLive() {
    setPublishing(true);
    YextApi.publish(locationId)
      .then(() => message.success("Your listing is live."))
      .catch((e) => message.error(yextErrorMessage(e, "Failed to publish your listing.")))
      .finally(() => {
        setPublishing(false);
        onLive();
      });
  }

  function confirm() {
    Modal.confirm({
      title: "Are you sure?",
      content: "Double-check that all info is correct.",
      okText: "Continue",
      cancelText: "Cancel",
      onOk: goLive,
    });
  }

  const hint = !profile
    ? undefined
    : missing.length
    ? `Missing: ${missing.join(", ")}`
    : saving
    ? "Saving your changes…"
    : undefined;

  return (
    <Tooltip title={hint}>
      <span className="listings-golive">
        <Button type="primary" size="large" loading={publishing} disabled={!ready} onClick={confirm}>
          Publish and Go Live
        </Button>
      </span>
    </Tooltip>
  );
}

function ClosedPill({ closure }: { closure?: YextTemporaryClosure }) {
  if (!closure?.from || !closure.through) return null;
  return (
    <span className="listings-closed-pill">
      <WarningFilled /> Temporarily closed from {formatClosureDate(closure.from)} –{" "}
      {formatClosureDate(closure.through)}
    </span>
  );
}

function formatClosureDate(iso: string): string {
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return iso;
  return `${Number(month)}/${Number(day)}/${year}`;
}

// Header pill reflecting draft-vs-live state: synced (green), unpublished changes (amber),
// or a failed transmit (red). Renders nothing until the first status loads.
function SyncPill({ sync }: { sync: YextSyncStatus | null }) {
  if (!sync) return null;
  if (sync.lastError) {
    return (
      <div className="sync-status-failed">
        <span className="sync-pill error">
          <ExclamationCircleFilled /> Sync failed
        </span>
        <span className="sync-error-detail">{sync.lastError}</span>
      </div>
    );
  }
  if (sync.hasUnpublishedChanges) {
    return (
      <span className="sync-pill pending">
        <span className="sync-dot" /> Unpublished changes
      </span>
    );
  }
  if (sync.syncedAt) {
    return (
      <span className="sync-pill ok">
        <CheckCircleFilled /> Synced {new Date(sync.syncedAt).toLocaleDateString()}
      </span>
    );
  }
  return null;
}
